import { FC, FormEvent, useEffect, useState } from "react";
import { Box, Button, Link, Stack, Typography } from "@mui/material";
import { MuiOtpInput } from "mui-one-time-password-input";
import { useVerifyResetCode } from "../../queries";
import { Link as RouterLink } from "react-router";
import { ROUTE } from "@/utils";

interface VerifyCodeFormProps {
  email: string;
  onComplete: (code: string) => void;
  onResend: () => void;
}

export const VerifyCodeForm: FC<VerifyCodeFormProps> = ({
  email,
  onComplete,
  onResend,
}) => {
  const { verifyResetCode, isVerifyResetCodeLoading } = useVerifyResetCode();
  const [code, setCode] = useState("");
  const [resendCountdown, setResendCountdown] = useState(120);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleVerifyCode = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (code.length === 4) {
      verifyResetCode({ email, code }, () => onComplete(code));
    }
  };

  const handleResendCode = () => {
    onResend();
    setResendCountdown(120);
  };

  const isCodeFormValid = code.length === 4;

  return (
    <Box component="form" width={"100%"} onSubmit={handleVerifyCode}>
      <Stack justifyContent={"flex-start"} width={"100%"}>
        <Typography variant="h4" mt={4}>
          Verify Reset Code
        </Typography>
        <Typography variant="body2" sx={{ mb: 4 }}>
          Enter the 4-digit code sent to your email
        </Typography>
      </Stack>

      <MuiOtpInput
        value={code}
        onChange={(newValue: string) => {
          if (newValue.match(/^[0-9]*$/)) {
            setCode(newValue);
          }
        }}
        length={4}
        sx={{ mb: 3 }}
        TextFieldsProps={{
          variant: "filled",
        }}
      />

      <Button
        fullWidth
        color="secondary"
        variant="contained"
        type="submit"
        loading={isVerifyResetCodeLoading}
        disabled={isVerifyResetCodeLoading || !isCodeFormValid}
        sx={{ mb: 2 }}
      >
        Continue
      </Button>

      <Box mt={1} textAlign="center">
        <Link
          component={RouterLink}
          to={ROUTE.AUTH_LOGIN}
          sx={{
            color: "#FFFFFF",
            textDecoration: "none",
          }}
        >
          Back to Login
        </Link>
      </Box>

      <Typography
        variant="body2"
        align="center"
        sx={{ mt: 1, userSelect: "none" }}
      >
        Didn’t receive the code?{" "}
        <Link
          component="button"
          variant="body2"
          underline="none"
          onClick={() => {
              setCode("")
              handleResendCode()
          }}
          disabled={resendCountdown > 0}
          sx={{
            cursor: resendCountdown === 0 ? "pointer" : "default",
            fontWeight: "500",
            ml: 0.5,
            color: "primary.main",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : "Resend"}
        </Link>
      </Typography>
    </Box>
  );
};
